import { useEffect, useState } from 'react'
import {
  api,
  ErrorDeApi,
  type CuentaBancaria,
  type EmparejamientoBancario as Emparejamiento,
  type MovimientoContable,
  type MovimientoPendiente,
  type PagoCfdi,
  type PartidaDeEmparejamiento,
  type PartidasPendientes,
  type PeriodoBancario,
  type PropuestaDeEmparejamiento,
} from '../servicios/api'
import { fechaContable } from '../fechas'

/** Desde aquí la confianza de una propuesta se considera alta y entra en «Confirmar las de confianza alta». */
export const CONFIANZA_ALTA = 80
const CONFIANZA_MEDIA = 50

const SIN_PERMISO = 'Tu rol no puede hacer este cambio: pide a un contador o a un administrador del despacho que lo haga.'

const mensaje = (e: unknown, respaldo: string) =>
  e instanceof ErrorDeApi ? (e.estado === 403 ? SIN_PERMISO : e.message) : respaldo

const pesos = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Con signo: positivo entró al banco, negativo salió. */
const conSigno = (n: number) => (n < 0 ? `−${pesos(-n)}` : pesos(n))

/** RN-37 cuadra al centavo: se compara en centavos enteros para no fallar por el redondeo de los flotantes. */
const centavos = (n: number) => Math.round(n * 100)

const suma = (importes: number[]) => importes.reduce((total, n) => total + centavos(n), 0)

const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

const clavePago = (p: { comprobanteId: string; indiceDePago: number }) => `${p.comprobanteId}#${p.indiceDePago}`

const uuidCorto = (uuid: string) => uuid.slice(0, 8)

function Confianza({ valor }: { valor: number }) {
  const [clase, texto] = valor >= CONFIANZA_ALTA ? ['vigente', 'Alta'] : valor >= CONFIANZA_MEDIA ? ['aviso', 'Media'] : ['neutra', 'Baja']
  return <span className={`insignia ${clase}`} title={`Confianza ${valor} de 100`}>{texto} · {valor}</span>
}

function DescripcionContable({ m }: { m: MovimientoContable }) {
  return (
    <>
      Póliza de {m.tipoPoliza} {m.folio} · {fechaContable(m.fecha)}
      <div className="sutil" style={{ fontSize: 11 }}>{m.concepto}{m.referencia ? ` · ref. ${m.referencia}` : ''}</div>
    </>
  )
}

function DescripcionPago({ p }: { p: PagoCfdi }) {
  return (
    <>
      CFDI de pago <span className="mono">{uuidCorto(p.uuid)}</span> · {fechaContable(p.fechaPago)}
      <div className="sutil" style={{ fontSize: 11 }}>{p.contraparte}</div>
      {p.facturas.length > 0 && (
        <div className="sutil" style={{ fontSize: 11 }}>
          Salda: {p.facturas.map((f) => `${[f.serie, f.folio].filter(Boolean).join('-') || uuidCorto(f.uuid)}${f.importePagado !== null ? ` (${pesos(f.importePagado)})` : ''}`).join(', ')}
        </div>
      )}
    </>
  )
}

function DescripcionBancaria({ m }: { m: MovimientoPendiente }) {
  return (
    <>
      {fechaContable(m.fecha)} · {m.concepto}
      {m.referencia && <div className="sutil mono" style={{ fontSize: 11 }}>ref. {m.referencia}</div>}
    </>
  )
}

/**
 * Emparejamiento de la conciliación bancaria (RF-1304, RF-1305, RF-1307): las propuestas del motor
 * para confirmar, el emparejamiento manual con agrupación (uno contra varios y varios contra uno) y
 * los emparejamientos hechos, que se pueden deshacer. Nada se concilia solo (RN-36) y todo cuadra
 * al centavo (RN-37): la pantalla no ofrece emparejar si la diferencia no es cero.
 */
export function EmparejamientoBancario({
  clienteId,
  cuenta,
  puedeEmparejar,
}: {
  clienteId: string
  cuenta: CuentaBancaria
  puedeEmparejar: boolean
}) {
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [periodo, setPeriodo] = useState<PeriodoBancario>({})
  const [pendientes, setPendientes] = useState<PartidasPendientes>()
  const [propuestas, setPropuestas] = useState<PropuestaDeEmparejamiento[]>()
  const [emparejamientos, setEmparejamientos] = useState<Emparejamiento[]>()
  const [incluirDeshechos, setIncluirDeshechos] = useState(false)
  const [bancarios, setBancarios] = useState<string[]>([])
  const [contables, setContables] = useState<string[]>([])
  const [pagos, setPagos] = useState<string[]>([])
  const [error, setError] = useState('')
  const [aviso, setAviso] = useState('')
  const [ocupado, setOcupado] = useState(false)

  async function cargar(p: PeriodoBancario = periodo, deshechos = incluirDeshechos) {
    try {
      const [pend, prop, emp] = await Promise.all([
        api.partidasPendientesBancarias(clienteId, cuenta.id, p),
        api.propuestasDeEmparejamiento(clienteId, cuenta.id, p),
        api.emparejamientosBancarios(clienteId, cuenta.id, { ...p, ...(deshechos && { incluirDeshechos: true }) }),
      ])
      setPendientes(pend)
      setPropuestas(prop)
      setEmparejamientos(emp)
      setBancarios([])
      setContables([])
      setPagos([])
    } catch (e) {
      setError(mensaje(e, 'No se pudo cargar el emparejamiento de la cuenta.'))
    }
  }

  useEffect(() => {
    void cargar({})
  }, [clienteId, cuenta.id])

  function aplicarPeriodo(evento: React.FormEvent) {
    evento.preventDefault()
    const nuevo: PeriodoBancario = { ...(desde && { desde }), ...(hasta && { hasta }) }
    setPeriodo(nuevo)
    setError('')
    void cargar(nuevo)
  }

  async function guardar(partidas: PartidaDeEmparejamiento[], exito: string) {
    setOcupado(true)
    setError('')
    setAviso('')
    try {
      await api.emparejar(clienteId, cuenta.id, partidas)
      setAviso(exito)
      await cargar()
    } catch (e) {
      setError(mensaje(e, 'No se pudo guardar el emparejamiento.'))
    } finally {
      setOcupado(false)
    }
  }

  const partidaDe = (p: PropuestaDeEmparejamiento): PartidaDeEmparejamiento => ({
    movimientosBancarios: [p.movimientoBancario.id],
    movimientosContables: p.contable ? [p.contable.id] : [],
    pagos: p.pago ? [{ comprobanteId: p.pago.comprobanteId, indiceDePago: p.pago.indiceDePago }] : [],
    origen: 'Automatico',
    confianza: p.confianza,
  })

  const altas = (propuestas ?? []).filter((p) => p.confianza >= CONFIANZA_ALTA)

  async function deshacer(e: Emparejamiento) {
    if (!window.confirm(`¿Deshacer este emparejamiento de ${conSigno(e.importe)}? Sus movimientos vuelven a pendientes y queda el rastro.`)) return
    setOcupado(true)
    setError('')
    setAviso('')
    try {
      await api.deshacerEmparejamiento(clienteId, cuenta.id, e.id)
      setAviso('Emparejamiento deshecho: sus movimientos volvieron a pendientes.')
      await cargar()
    } catch (err) {
      setError(mensaje(err, 'No se pudo deshacer el emparejamiento.'))
    } finally {
      setOcupado(false)
    }
  }

  function alternar(lista: string[], fijar: (l: string[]) => void, clave: string) {
    fijar(lista.includes(clave) ? lista.filter((x) => x !== clave) : [...lista, clave])
  }

  const seleccionBancaria = (pendientes?.bancarios ?? []).filter((m) => bancarios.includes(m.id))
  const seleccionContable = (pendientes?.contables ?? []).filter((m) => contables.includes(m.id))
  const seleccionDePagos = (pendientes?.pagos ?? []).filter((p) => pagos.includes(clavePago(p)))
  const sumaBancaria = suma(seleccionBancaria.map((m) => m.importe))
  const sumaDelOtroLado = suma([...seleccionContable.map((m) => m.importe), ...seleccionDePagos.map((p) => p.importe)])
  const diferencia = sumaBancaria - sumaDelOtroLado
  const mezclado = seleccionContable.length > 0 && seleccionDePagos.length > 0
  const sinOtroLado = seleccionContable.length === 0 && seleccionDePagos.length === 0

  const porQueNo =
    seleccionBancaria.length === 0 ? 'Elige al menos un movimiento bancario.'
      : sinOtroLado ? 'Elige su pareja: movimientos de póliza o pagos de CFDI.'
        : mezclado ? 'Empareja contra pólizas o contra pagos de CFDI, no los dos a la vez.'
          : diferencia !== 0 ? `No cuadra: hay una diferencia de ${conSigno(diferencia / 100)}. Un emparejamiento cuadra al centavo.`
            : ''

  function emparejarSeleccion() {
    const n = seleccionBancaria.length
    void guardar([{
      movimientosBancarios: seleccionBancaria.map((m) => m.id),
      movimientosContables: seleccionContable.map((m) => m.id),
      pagos: seleccionDePagos.map((p) => ({ comprobanteId: p.comprobanteId, indiceDePago: p.indiceDePago })),
      origen: 'Manual',
      confianza: null,
    }], `Emparejaste ${n} ${n === 1 ? 'movimiento bancario' : 'movimientos bancarios'} por ${conSigno(sumaBancaria / 100)}.`)
  }

  const sinPropuestas = pendientes && pendientes.bancarios.length === 0
    ? 'No hay movimientos bancarios pendientes en el periodo: todos están emparejados, o no se ha importado ninguno.'
    : `Hay ${pendientes?.bancarios.length ?? 0} ${pendientes?.bancarios.length === 1 ? 'movimiento bancario pendiente' : 'movimientos bancarios pendientes'} y ninguno tiene una pareja exacta al centavo en la cuenta contable ${cuenta.cuentaContable} ni en los CFDI de pago. Emparéjalos a mano abajo, o revisa que la póliza esté registrada en esa cuenta.`

  return (
    <section aria-labelledby="titulo-emparejamiento">
      <h2 id="titulo-emparejamiento">Emparejamiento</h2>
      <p className="nota">
        El motor propone parejas exactas al centavo entre los movimientos del banco y los de la cuenta contable{' '}
        {cuenta.cuentaContable}, o los pagos de CFDI. Nada se concilia hasta que lo confirmas.
      </p>

      <form className="filtros" onSubmit={aplicarPeriodo} aria-label="Periodo del emparejamiento">
        <label>Desde <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label>Hasta <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        <button type="submit" className="secundario">Aplicar periodo</button>
      </form>

      {error && <p className="error" role="alert">{error}</p>}
      {aviso && <p className="aviso" role="status">{aviso}</p>}

      <h3>Propuestas del motor</h3>
      {!propuestas && !error && <p className="sutil">Cargando…</p>}
      {propuestas && propuestas.length === 0 && <p className="vacio">{sinPropuestas}</p>}
      {propuestas && propuestas.length > 0 && (
        <>
          {puedeEmparejar && altas.length > 0 && (
            <p>
              <button className="primario" disabled={ocupado}
                onClick={() => void guardar(altas.map(partidaDe),
                  `Confirmaste ${altas.length} ${altas.length === 1 ? 'propuesta' : 'propuestas'} de confianza alta.`)}>
                Confirmar las de confianza alta ({altas.length})
              </button>
            </p>
          )}
          <div className="tabla-envoltura">
            <table className="tabla" aria-label="Propuestas del motor">
              <thead>
                <tr>
                  <th scope="col">Movimiento bancario</th>
                  <th scope="col" className="num">Importe</th>
                  <th scope="col">Pareja propuesta</th>
                  <th scope="col">Confianza</th>
                  <th scope="col">Por qué</th>
                  {puedeEmparejar && <th scope="col">Acción</th>}
                </tr>
              </thead>
              <tbody>
                {propuestas.map((p) => (
                  <tr key={p.movimientoBancario.id}>
                    <td><DescripcionBancaria m={p.movimientoBancario} /></td>
                    <td className="num mono">{conSigno(p.movimientoBancario.importe)}</td>
                    <td>{p.contable ? <DescripcionContable m={p.contable} /> : p.pago && <DescripcionPago p={p.pago} />}</td>
                    <td><Confianza valor={p.confianza} /></td>
                    <td><ul className="lista-compacta">{p.motivos.map((m) => <li key={m}>{m}</li>)}</ul></td>
                    {puedeEmparejar && (
                      <td>
                        <button className="enlace" disabled={ocupado}
                          onClick={() => void guardar([partidaDe(p)], 'Propuesta confirmada.')}
                          aria-label={`Confirmar la propuesta de ${p.movimientoBancario.concepto}`}>
                          Confirmar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3>Emparejamiento manual</h3>
      {pendientes && (
        <>
          <p className="nota">
            Para depósitos agrupados o pagos parciales: elige uno o varios movimientos bancarios y su pareja, movimientos
            de póliza o pagos de CFDI. La suma de los dos lados tiene que ser idéntica.
          </p>
          <ListaPendiente
            titulo="Movimientos bancarios pendientes"
            vacio="No hay movimientos bancarios pendientes en el periodo."
            filas={pendientes.bancarios.map((m) => ({ clave: m.id, descripcion: <DescripcionBancaria m={m} />, importe: m.importe, etiqueta: m.concepto }))}
            elegidas={bancarios}
            alElegir={puedeEmparejar ? (c) => alternar(bancarios, setBancarios, c) : undefined}
          />
          <ListaPendiente
            titulo="Movimientos de póliza sin emparejar"
            vacio={`No hay movimientos de póliza sin emparejar en la cuenta contable ${cuenta.cuentaContable} en el periodo.`}
            filas={pendientes.contables.map((m) => ({ clave: m.id, descripcion: <DescripcionContable m={m} />, importe: m.importe, etiqueta: `póliza de ${m.tipoPoliza} ${m.folio}` }))}
            elegidas={contables}
            alElegir={puedeEmparejar ? (c) => alternar(contables, setContables, c) : undefined}
          />
          <ListaPendiente
            titulo="Pagos de CFDI sin emparejar"
            vacio="No hay pagos de CFDI sin emparejar en el periodo."
            filas={pendientes.pagos.map((p) => ({ clave: clavePago(p), descripcion: <DescripcionPago p={p} />, importe: p.importe, etiqueta: `CFDI de pago ${uuidCorto(p.uuid)}` }))}
            elegidas={pagos}
            alElegir={puedeEmparejar ? (c) => alternar(pagos, setPagos, c) : undefined}
          />

          {puedeEmparejar && (
            <div className="tarjeta" aria-label="Cuadre del emparejamiento">
              <p>
                Banco: <span className="mono">{conSigno(sumaBancaria / 100)}</span> · Otro lado:{' '}
                <span className="mono">{conSigno(sumaDelOtroLado / 100)}</span> · Diferencia:{' '}
                <span className="mono">{conSigno(diferencia / 100)}</span>
              </p>
              {porQueNo && <p className="sutil">{porQueNo}</p>}
              <button className="primario" disabled={ocupado || porQueNo !== ''} onClick={emparejarSeleccion}>Emparejar</button>
            </div>
          )}
        </>
      )}

      <h3>Emparejamientos</h3>
      <label>
        <input type="checkbox" checked={incluirDeshechos}
          onChange={(e) => {
            setIncluirDeshechos(e.target.checked)
            void cargar(periodo, e.target.checked)
          }} />{' '}
        Ver también los deshechos
      </label>
      {emparejamientos && emparejamientos.length === 0 && (
        <p className="vacio">
          {incluirDeshechos ? 'No hay emparejamientos en el periodo.' : 'No hay emparejamientos vigentes en el periodo.'}
        </p>
      )}
      {emparejamientos && emparejamientos.length > 0 && (
        <div className="tabla-envoltura">
          <table className="tabla" aria-label="Emparejamientos">
            <thead>
              <tr>
                <th scope="col">Hecho</th>
                <th scope="col">Origen</th>
                <th scope="col" className="num">Importe</th>
                <th scope="col">Banco</th>
                <th scope="col">Pareja</th>
                <th scope="col">Estado</th>
                {puedeEmparejar && <th scope="col">Acción</th>}
              </tr>
            </thead>
            <tbody>
              {emparejamientos.map((e) => (
                <tr key={e.id} className={e.vigente ? undefined : 'inactiva'}>
                  <td>{instante(e.creadoEnUtc)}</td>
                  <td>
                    {e.origen === 'Automatico' ? 'Propuesta confirmada' : 'Manual'}
                    {e.confianza !== null && <div><Confianza valor={e.confianza} /></div>}
                  </td>
                  <td className="num mono">{conSigno(e.importe)}</td>
                  <td>{e.bancarios.map((m) => <div key={m.id}><DescripcionBancaria m={m} /></div>)}</td>
                  <td>
                    {e.contables.map((m) => <div key={m.id}><DescripcionContable m={m} /></div>)}
                    {e.pagos.map((p) => <div key={clavePago(p)}><DescripcionPago p={p} /></div>)}
                  </td>
                  <td>{e.vigente ? 'Vigente' : `Deshecho${e.deshechoEnUtc ? ` el ${instante(e.deshechoEnUtc)}` : ''}`}</td>
                  {puedeEmparejar && (
                    <td>
                      {e.vigente && (
                        <button className="enlace" disabled={ocupado} onClick={() => void deshacer(e)}
                          aria-label={`Deshacer el emparejamiento de ${conSigno(e.importe)}`}>
                          Deshacer
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function ListaPendiente({
  titulo,
  vacio,
  filas,
  elegidas,
  alElegir,
}: {
  titulo: string
  vacio: string
  filas: { clave: string; descripcion: React.ReactNode; importe: number; etiqueta: string }[]
  elegidas: string[]
  alElegir?: (clave: string) => void
}) {
  return (
    <>
      <h4>{titulo}</h4>
      {filas.length === 0 ? (
        <p className="vacio">{vacio}</p>
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla" aria-label={titulo}>
            <thead>
              <tr>
                {alElegir && <th scope="col">Elegir</th>}
                <th scope="col">Movimiento</th>
                <th scope="col" className="num">Importe</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.clave}>
                  {alElegir && (
                    <td>
                      <input type="checkbox" checked={elegidas.includes(f.clave)} onChange={() => alElegir(f.clave)}
                        aria-label={`Elegir ${f.etiqueta} de ${conSigno(f.importe)}`} />
                    </td>
                  )}
                  <td>{f.descripcion}</td>
                  <td className="num mono">{conSigno(f.importe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
